import { isAndroid, isDefined, isFunction } from '@philips-internal/oswald-ui-utils';
import { LanguageService } from '../language-service/language-service.js';
import { OswaldSpeechSynthesisUtterance } from './speech-synthesis.js';
import { isFirefox, isKaiOS, isSafari } from './text-to-speech-utils.js';
const NOT_ALLOWED_ERROR = 'not-allowed';
let timeoutSpeechReset;
/**
 * Creates a timeout to recursevely call `pause` and `resume` every 5 seconds as a workaround to some voices stopping when reading long texts.
 * https://stackoverflow.com/questions/21947730/chrome-speech-synthesis-with-longer-texts
 *
 * @param state - the internal state controll of the Text to Speech Service
 * @param target - reference to the instance of the message being read
 */
function speechTimerReset(state, target) {
    clearTimeout(timeoutSpeechReset);
    // prevent memory-leak in case utterance is deleted, while this is ongoing
    if (!target && timeoutSpeechReset) {
        return;
    }
    const isSpeaking = speechSynthesis.speaking || state.speaking;
    const isPaused = speechSynthesis.paused || state.paused;
    if (isSpeaking && !isPaused) {
        speechSynthesis.pause();
        speechSynthesis.resume();
    }
    timeoutSpeechReset = window.setTimeout(() => {
        speechTimerReset(state, target);
    }, 5000);
}
function textToSpeechError(message) {
    return new Error(`[Text to Speech Service]: ${message}`);
}
function isSameBaseLanguage(firstLanguage, secondLanguage) {
    // On mobile voice languages use '_' as separator
    return firstLanguage.split(/[-_]/)[0] === secondLanguage.split(/[-_]/)[0];
}
function getCurrentLanguageVoices() {
    return speechSynthesis.getVoices().filter(voice => {
        return isSameBaseLanguage(LanguageService.getCurrentLanguage(), voice.lang);
    });
}
function createTextToSpeechService() {
    let queue = [];
    let currentMessage;
    let ensureInteractionPromise;
    let initialized = false;
    let voices = [];
    const patches = {
        isAndroid: isAndroid(),
        isFirefox: isFirefox() || isKaiOS(),
        isSafari: isSafari(),
    };
    const state = {
        paused: false,
        speaking: false,
    };
    const cancelAll = () => {
        queue = [];
        state.paused = false;
        state.speaking = false;
        speechSynthesis.cancel();
    };
    const cancelCurrent = () => {
        state.paused = false;
        state.speaking = false;
        currentMessage?.cancel();
    };
    const ensureSpeechPermission = () => {
        if (isDefined(ensureInteractionPromise)) {
            return ensureInteractionPromise;
        }
        ensureInteractionPromise = new Promise((resolve, reject) => {
            const speechUtter = new SpeechSynthesisUtterance('');
            speechUtter.onend = () => {
                resolve(true);
            };
            speechUtter.onerror = (event) => {
                if (event.error === NOT_ALLOWED_ERROR) {
                    return resolve(false);
                }
                reject(event);
            };
            speechSynthesis.speak(speechUtter);
        });
        return ensureInteractionPromise;
    };
    const getQueue = () => {
        return [...queue];
    };
    const getVoices = () => {
        if (!hasBeenInitialized()) {
            throw textToSpeechError('Service not initialized');
        }
        return voices;
    };
    const hasBeenInitialized = () => {
        return initialized;
    };
    const initialize = () => {
        if (hasBeenInitialized()) {
            return;
        }
        if (!window.speechSynthesis) {
            throw textToSpeechError('Device not supported');
        }
        speechSynthesis.onvoiceschanged = () => {
            voices = getCurrentLanguageVoices();
        };
        LanguageService.addListener(() => {
            voices = getCurrentLanguageVoices();
        });
        voices = getCurrentLanguageVoices();
        initialized = true;
    };
    const isPaused = () => {
        return state.paused;
    };
    const isSpeaking = () => {
        return state.speaking;
    };
    const pause = () => {
        state.paused = true;
        state.speaking = false;
        // exec pause on Android causes speech to end but not to fire end-event so we simply do it manually instead of pausing
        if (patches.isAndroid) {
            state.paused = false;
            return speechSynthesis.cancel();
        }
        speechSynthesis.pause();
    };
    const resume = () => {
        state.paused = false;
        state.speaking = Boolean(currentMessage);
        speechSynthesis.resume();
    };
    const playNext = () => {
        clearTimeout(timeoutSpeechReset);
        currentMessage = queue.shift();
        if (!currentMessage) {
            return;
        }
        if (currentMessage.isCancelled()) {
            playNext();
        }
        else {
            currentMessage.addEventListener('start', () => {
                // Ignoring the larger text fix for chrome voices in these as the fix breaks in them
                if (!patches.isFirefox && !patches.isSafari && !patches.isAndroid) {
                    speechTimerReset(state, currentMessage);
                }
            });
            currentMessage.addEventListener('end', () => {
                state.paused = false;
                state.speaking = false;
                playNext();
            });
            currentMessage.addEventListener('error', (event) => {
                state.paused = false;
                state.speaking = false;
                // Just logging to keep reading texts on queue
                // eslint-disable-next-line no-console
                console.error(textToSpeechError(event.error));
                playNext();
            });
            state.paused = false;
            state.speaking = true;
            speechSynthesis.speak(currentMessage.getUtterance());
        }
    };
    const speak = (options) => {
        const { onBoundary, onEnd, onError, onMark, onPause, onResume, onStart, voice, ...utteranceOptions } = options;
        if (voice && !isSameBaseLanguage(LanguageService.getCurrentLanguage(), voice.lang)) {
            throw textToSpeechError('The voice must follow the application language.');
        }
        const message = new OswaldSpeechSynthesisUtterance({
            voice,
            ...utteranceOptions,
        });
        const eventListeners = [
            { event: 'boundary', listener: onBoundary },
            { event: 'end', listener: onEnd },
            // Casting to keep the simplicity of the code
            { event: 'error', listener: onError },
            { event: 'mark', listener: onMark },
            { event: 'pause', listener: onPause },
            { event: 'resume', listener: onResume },
            { event: 'start', listener: onStart },
        ];
        for (const { event, listener } of eventListeners) {
            if (!isFunction(listener)) {
                continue;
            }
            message.addEventListener(event, listener);
        }
        queue.push(message);
        if (!state.speaking) {
            playNext();
        }
        return message;
    };
    return {
        cancelAll,
        cancelCurrent,
        ensureSpeechPermission,
        getQueue,
        getVoices,
        hasBeenInitialized,
        initialize,
        isPaused,
        isSpeaking,
        pause,
        resume,
        speak,
    };
}
const TextToSpeechService = createTextToSpeechService();
export { TextToSpeechService, };
//# sourceMappingURL=text-to-speech-service.js.map